(() => {
  if (window.GlowinexBYLMarkerlessV3) {
    window.GlowinexBYLMarkerlessV3.scheduleSync?.(0);
    return;
  }

  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const updateUrl = `${root}cart/update.js`;

  const BUNDLE_PROPERTY = '_Glowinex Bundle';
  const SIZE_PROPERTY = '_Bundle Size';
  const TIER_PROPERTY = '_Bundle Discount Tier';
  const ITEM_PROPERTY = '_Bundle Item';
  const VERSION_PROPERTY = '_Bundle Version';
  const SOURCE_PROPERTY = '_Bundle Source';
  const GROUP_PROPERTY = '_Bundle Group';
  const LINE_TOKEN_PROPERTY = '_Bundle Line Token';
  const SOURCE_VALUE = 'Build Your Light';
  const VERSION_VALUE = 'v10e3';
  const VALID_TIERS = { 2: '10%', 3: '20%', 4: '30%' };
  const BYL_CODES = {
    2: 'GLX-BYL-2-R7K4Q9M2',
    3: 'GLX-BYL-3-V8N2P6R5',
    4: 'GLX-BYL-4-K9T3W7H4',
  };
  const BYL_CODE_SET = new Set(Object.values(BYL_CODES));

  const ATTR_MANAGED = '_Glowinex BYL Managed';
  const ATTR_CODE = '_Glowinex BYL Code';
  const ATTR_GROUP = '_Glowinex BYL Group';
  const ATTR_SIZE = '_Glowinex BYL Size';
  const ATTR_TIER = '_Glowinex BYL Tier';
  const ATTR_SOURCE = '_Glowinex Bundle Source';
  const ATTR_VERSION = '_Glowinex Bundle Version';

  let syncTimer = null;
  let syncPromise = null;

  const GUARD_KEY = 'glx-byl-v10e-active';
  let lastKnownBundleMarker = false;

  try {
    lastKnownBundleMarker = window.localStorage?.getItem(GUARD_KEY) === '1';
  } catch (error) {
    lastKnownBundleMarker = false;
  }

  function setBundleGuard(active) {
    lastKnownBundleMarker = Boolean(active);
    try {
      if (lastKnownBundleMarker) window.localStorage?.setItem(GUARD_KEY, '1');
      else window.localStorage?.removeItem(GUARD_KEY);
    } catch (error) {
      // Local storage is only a checkout safety hint.
    }
  }

  function bundleGuardActive() {
    return lastKnownBundleMarker;
  }

  function createToken(prefix = 'glx') {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }

  function valueOf(formData, key) {
    const value = formData.get(key);
    return value == null ? '' : String(value);
  }

  function bundleIndexes(formData) {
    const indexes = [];
    for (const [key, value] of formData.entries()) {
      const match = /^items\[(\d+)\]\[properties\]\[_Glowinex Bundle\]$/.exec(String(key));
      if (!match || String(value).toLowerCase() !== 'yes') continue;
      indexes.push(Number(match[1]));
    }
    return Array.from(new Set(indexes)).sort((a, b) => a - b);
  }

  function validateGeneratedBundle(formData, indexes) {
    const count = indexes.length;
    const expectedTier = VALID_TIERS[count];
    if (!expectedTier || count < 2 || count > 4) {
      throw new Error('Glowinex Build Your Light validation failed. Please rebuild the bundle.');
    }

    indexes.forEach((index, position) => {
      const prefix = `items[${index}][properties]`;
      const size = valueOf(formData, `${prefix}[${SIZE_PROPERTY}]`);
      const tier = valueOf(formData, `${prefix}[${TIER_PROPERTY}]`);
      const itemNumber = valueOf(formData, `${prefix}[${ITEM_PROPERTY}]`);
      const quantity = valueOf(formData, `items[${index}][quantity]`);
      if (
        size !== String(count)
        || tier !== expectedTier
        || itemNumber !== String(position + 1)
        || quantity !== '1'
      ) {
        throw new Error('Glowinex Build Your Light validation failed. Every bundle item must be a separate quantity-1 line.');
      }
    });
  }

  function stampBundle(formData, indexes) {
    validateGeneratedBundle(formData, indexes);
    const groupId = createToken('glx-byl-group');

    indexes.forEach((index, position) => {
      const prefix = `items[${index}][properties]`;
      formData.set(`${prefix}[${VERSION_PROPERTY}]`, VERSION_VALUE);
      formData.set(`${prefix}[${SOURCE_PROPERTY}]`, SOURCE_VALUE);
      formData.set(`${prefix}[${GROUP_PROPERTY}]`, groupId);
      formData.set(`${prefix}[${LINE_TOKEN_PROPERTY}]`, createToken(`glx-byl-line-${position + 1}`));
    });

    setBundleGuard(true);
    return groupId;
  }

  async function readCart() {
    const response = await nativeFetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to read cart.');
    return response.json();
  }

  async function writeCart(discount, attributes) {
    const response = await nativeFetch(updateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ discount, attributes }),
    });
    if (!response.ok) throw new Error('Unable to update Build Your Light cart state.');
    return response.json();
  }

  function propertiesOf(item) {
    return item?.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function cartDiscountCodes(cart) {
    return (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
      .map((entry) => String(entry?.code || '').trim())
      .filter(Boolean);
  }

  function activeBylCode(cart) {
    const entry = (Array.isArray(cart?.discount_codes) ? cart.discount_codes : []).find((item) => {
      const code = String(item?.code || '').trim();
      return BYL_CODE_SET.has(code) && item?.applicable !== false;
    });
    return String(entry?.code || '').trim();
  }

  function nonBylCodes(cart) {
    return cartDiscountCodes(cart).filter((code) => !BYL_CODE_SET.has(code));
  }

  function hasBundleMarker(cart) {
    return (Array.isArray(cart?.items) ? cart.items : []).some((item) => {
      const p = propertiesOf(item);
      return String(p[BUNDLE_PROPERTY] || '').toLowerCase() === 'yes';
    });
  }

  function analyzeCart(cart) {
    const allItems = Array.isArray(cart?.items) ? cart.items : [];
    const bundleMarkerPresent = hasBundleMarker(cart);
    const groups = new Map();
    let bundleQuantity = 0;

    for (const item of allItems) {
      const p = propertiesOf(item);
      if (String(p[BUNDLE_PROPERTY] || '').toLowerCase() !== 'yes') continue;
      if (String(p[VERSION_PROPERTY] || '') !== VERSION_VALUE) continue;
      if (String(p[SOURCE_PROPERTY] || '') !== SOURCE_VALUE) continue;

      const groupId = String(p[GROUP_PROPERTY] || '').trim();
      const lineToken = String(p[LINE_TOKEN_PROPERTY] || '').trim();
      const size = Number(p[SIZE_PROPERTY]);
      const tier = String(p[TIER_PROPERTY] || '');
      const itemNumber = Number(p[ITEM_PROPERTY]);
      const quantity = Number(item?.quantity || 0);

      if (!groupId || !lineToken || !VALID_TIERS[size] || tier !== VALID_TIERS[size]) continue;

      bundleQuantity += quantity;
      if (!groups.has(groupId)) groups.set(groupId, []);
      groups.get(groupId).push({ item, size, tier, itemNumber, quantity, lineToken });
    }

    if (groups.size !== 1) {
      return { eligible: false, bundleMarkerPresent, reason: groups.size ? 'multiple-groups' : 'no-valid-group' };
    }

    const [groupId, entries] = groups.entries().next().value;
    const size = entries[0]?.size;
    if (!VALID_TIERS[size] || entries.length !== size) {
      return { eligible: false, bundleMarkerPresent, reason: 'wrong-line-count' };
    }

    if (!entries.every((entry) => (
      entry.size === size
      && entry.tier === VALID_TIERS[size]
      && entry.quantity === 1
      && Boolean(entry.lineToken)
    ))) {
      return { eligible: false, bundleMarkerPresent, reason: 'invalid-line-state' };
    }

    const itemNumbers = new Set(entries.map((entry) => entry.itemNumber));
    const lineTokens = new Set(entries.map((entry) => entry.lineToken));
    if (itemNumbers.size !== size) return { eligible: false, bundleMarkerPresent, reason: 'duplicate-item-number' };
    if (lineTokens.size !== size) return { eligible: false, bundleMarkerPresent, reason: 'duplicate-line-token' };

    for (let index = 1; index <= size; index += 1) {
      if (!itemNumbers.has(index)) return { eligible: false, bundleMarkerPresent, reason: 'missing-item-number' };
    }

    const cartQuantity = allItems.reduce((total, item) => total + Number(item?.quantity || 0), 0);
    if (cartQuantity !== size || bundleQuantity !== size || allItems.length !== size) {
      return { eligible: false, bundleMarkerPresent, reason: 'mixed-cart' };
    }

    return {
      eligible: true,
      bundleMarkerPresent: true,
      groupId,
      size,
      tier: VALID_TIERS[size],
      code: BYL_CODES[size],
    };
  }

  function managed(cart) {
    return String(cart?.attributes?.[ATTR_MANAGED] || '').toLowerCase() === 'yes';
  }

  function clearAttributes() {
    return {
      [ATTR_MANAGED]: '',
      [ATTR_CODE]: '',
      [ATTR_GROUP]: '',
      [ATTR_SIZE]: '',
      [ATTR_TIER]: '',
      [ATTR_SOURCE]: '',
      [ATTR_VERSION]: '',
    };
  }

  function activeAttributes(state) {
    return {
      [ATTR_MANAGED]: 'Yes',
      [ATTR_CODE]: state.code,
      [ATTR_GROUP]: state.groupId,
      [ATTR_SIZE]: String(state.size),
      [ATTR_TIER]: state.tier,
      [ATTR_SOURCE]: SOURCE_VALUE,
      [ATTR_VERSION]: VERSION_VALUE,
    };
  }

  function attributesMatch(cart, state) {
    const attrs = cart?.attributes || {};
    return managed(cart)
      && String(attrs[ATTR_CODE] || '') === state.code
      && String(attrs[ATTR_GROUP] || '') === state.groupId
      && String(attrs[ATTR_SIZE] || '') === String(state.size)
      && String(attrs[ATTR_TIER] || '') === state.tier
      && String(attrs[ATTR_SOURCE] || '') === SOURCE_VALUE
      && String(attrs[ATTR_VERSION] || '') === VERSION_VALUE;
  }

  function publishCartSync(cart, state) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'glx-byl',
        cartData: cart,
        state,
      });
    }
    try {
      document.dispatchEvent(new CustomEvent('glx:byl:cart-synced', { detail: { state } }));
    } catch (error) {
      // Non-critical UI event.
    }
  }

  async function sync(options = {}) {
    const strict = Boolean(options?.strict);
    // A scheduled sync can still be reading the pre-add (empty) cart.
    // A strict post-add check must not reuse that stale in-flight result.
    if (options?.fresh && syncPromise) {
      try {
        await syncPromise;
      } catch (previousError) {
        // Re-read the cart even if the earlier background synchronization failed.
        console.warn('[Glowinex BYL] Retrying a fresh bundle verification.', previousError);
      }
    }

    if (!syncPromise) {
      syncPromise = (async () => {
        const cart = await readCart();
        const state = analyzeCart(cart);
        setBundleGuard(state.bundleMarkerPresent);
        const currentBylCode = activeBylCode(cart);

        if (state.eligible) {
          if (!attributesMatch(cart, state) || currentBylCode !== state.code) {
            const updatedCart = await writeCart(state.code, activeAttributes(state));
            if (activeBylCode(updatedCart) !== state.code) {
              throw new Error('Build Your Light discount could not be verified on the Shopify cart.');
            }
            publishCartSync(updatedCart, state);
          }
          return state;
        }

        if (managed(cart) || currentBylCode) {
          const keepCodes = nonBylCodes(cart);
          const updatedCart = await writeCart(keepCodes.join(','), clearAttributes());
          publishCartSync(updatedCart, state);
        }

        return state;
      })().finally(() => {
        syncPromise = null;
      });
    }

    try {
      return await syncPromise;
    } catch (error) {
      console.warn('[Glowinex BYL] Markerless discount sync failed.', error);
      if (strict) throw error;
      return null;
    }
  }

  function scheduleSync(delay = 80) {
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncTimer = null;
      sync();
    }, delay);
  }

  async function prepareForBundle() {
    const cart = await readCart();
    const quantity = (cart?.items || []).reduce((sum, item) => sum + Number(item?.quantity || 0), 0);

    if (quantity > 0) {
      const error = new Error('Build Your Light savings require an empty cart. Please clear your cart before starting a new bundle.');
      error.name = 'GlowinexBundleCartNotEmptyError';
      throw error;
    }

    return true;
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(?:add|change|update|clear)\.js(?:\?|$)/.test(String(url));
  }

  window.fetch = function glxMarkerlessFetchV3(input, init = {}) {
    try {
      const body = init?.body;
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        const indexes = bundleIndexes(body);
        if (indexes.length) stampBundle(body, indexes);
      }
    } catch (error) {
      console.error('[Glowinex BYL] Bundle integrity check failed.', error);
      return Promise.reject(error);
    }

    const responsePromise = nativeFetch(input, init);
    if (!isCartMutationUrl(requestUrl(input))) return responsePromise;

    return responsePromise.then((response) => {
      if (response?.ok) scheduleSync(40);
      return response;
    });
  };

  const api = {
    sync,
    scheduleSync,
    prepareForBundle,
    analyzeCart,
    bundleGuardActive,
    version: VERSION_VALUE,
  };

  window.GlowinexBYLMarkerlessV3 = api;
  window.GlowinexBYLMarkerlessV2 = api;
  window.__glxBundleV10EPreludeLoaded = true;

  window.addEventListener('pageshow', () => scheduleSync(30));
  document.addEventListener('DOMContentLoaded', () => scheduleSync(30));
  document.addEventListener('shopify:cart:discount-update', () => scheduleSync(0));
  document.addEventListener('glx:byl:sync', () => scheduleSync(0));

  window.setTimeout(() => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync(0));
    }
    scheduleSync(0);
  }, 0);
})();